import { translate } from '@fulcrum/lib';
// eslint-disable-next-line @nx/enforce-module-boundaries -- Generated SDK needed for follow ACL grant
import type { Client } from '@data-access/connect/tc-issue-management';
// eslint-disable-next-line @nx/enforce-module-boundaries -- Generated SDK needed for follow ACL grant
import {
  getIssuePermissions,
  patchIssuePermissions,
} from '@data-access/connect/tc-issue-management';
import {
  type IssueAclMap,
  type IssueAccessBucket,
  type IPermissionEntry,
  ISSUE_ACCESS_BUCKETS,
  PERMISSION_CONSTANTS,
} from '../types/issue-permissions.types';
import type { IProjectUser } from './util';

const EDIT_ACCESS_WEIGHT = 2;
const VIEW_ACCESS_WEIGHT = 1;
const AVATAR_INITIALS_MIN_NAME_PARTS = 2;
const AVATAR_INITIALS_FALLBACK_LENGTH = 2;

export const canDeleteIssueFromActions = (
  actions?: readonly string[]
): boolean => {
  if (!actions || !Array.isArray(actions)) return false;
  return actions.includes('issue:Delete') || actions.includes('ISSUE_DELETE');
};

export const canUpdatePermissionsFromActions = (
  actions: readonly string[] | undefined,
  isIssueAclEnabled: boolean
): boolean => {
  if (!isIssueAclEnabled) return false;
  if (!actions || !Array.isArray(actions)) return false;
  return (
    actions.includes('issue:UpdatePermissions') ||
    actions.includes('ISSUE_UPDATE_PERMISSIONS')
  );
};

export interface GrantReadAccessOnFollowParams {
  readonly client: Client;
  readonly contextId: string;
  readonly issueId: string;
  readonly currentUserTiduuid: string;
}

export async function grantReadAccessOnFollowForIssue(
  params: GrantReadAccessOnFollowParams
): Promise<void> {
  const currentUserIdentity = `${PERMISSION_CONSTANTS.USER_PREFIX}${params.currentUserTiduuid}`;

  const response = await getIssuePermissions({
    client: params.client,
    path: { contextId: params.contextId, issueId: params.issueId },
    query: { fields: ['inheritedAcl'] },
  });

  if (!response.data) return;

  const acl = parseExplicitIssueAcl(response.data.acl);
  const alreadyInAcl = ISSUE_ACCESS_BUCKETS.some((bucket) =>
    acl[bucket]?.includes(currentUserIdentity)
  );
  if (alreadyInAcl) return;

  const updatedAcl: IssueAclMap = {
    ...acl,
    view: [...(acl.view ?? []), currentUserIdentity],
  };

  await patchIssuePermissions({
    client: params.client,
    path: { contextId: params.contextId, issueId: params.issueId },
    body: buildPatchPermissionsBody(normalizeIssueAcl(updatedAcl)),
  });
}

export const getAccessWeight = (accessLevel: IssueAccessBucket): number => {
  return accessLevel === 'edit' ? EDIT_ACCESS_WEIGHT : VIEW_ACCESS_WEIGHT;
};

export const normalizeIssueAcl = (
  acl: IssueAclMap
): { edit: string[]; view: string[] } => ({
  edit: acl.edit ?? [],
  view: acl.view ?? [],
});

/**
 * Parses the `acl` field from an `IssuePermissions` API response.
 * The generated SDK types `IssuePermissions.acl` as `'edit' | 'view'` (string union),
 * but the actual runtime shape is a map `{ edit?: string[], view?: string[] }`.
 * Centralizing this cast isolates the codegen workaround to one place.
 */
export const parseExplicitIssueAcl = (rawAcl: unknown): IssueAclMap =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generated IssuePermissions.acl is incorrectly typed as string union instead of map (codegen bug)
  (rawAcl ?? {}) as any as IssueAclMap;

/**
 * Wraps a normalized ACL map in the shape expected by `patchIssuePermissions`.
 * The generated `IssuePermissionsPatchRequest.acl` is typed as `'edit' | 'view'`
 * but the API actually accepts the full map `{ edit: string[], view: string[] }`.
 */
export const buildPatchPermissionsBody = (
  normalizedAcl: ReturnType<typeof normalizeIssueAcl>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generated IssuePermissionsPatchRequest.acl is incorrectly typed as string union instead of map (codegen bug)
): { acl: 'edit' | 'view' } => ({ acl: normalizedAcl }) as any;

export const mergeIssueAcls = (
  aclA: IssueAclMap,
  aclB: IssueAclMap
): IssueAclMap => {
  const identityAccessMap = new Map<string, IssueAccessBucket>();

  const applyAcl = (acl: IssueAclMap): void => {
    ISSUE_ACCESS_BUCKETS.forEach((bucket) => {
      acl[bucket]?.forEach((identity) => {
        const currentAccess = identityAccessMap.get(identity);
        if (
          !currentAccess ||
          getAccessWeight(bucket) > getAccessWeight(currentAccess)
        ) {
          identityAccessMap.set(identity, bucket);
        }
      });
    });
  };

  applyAcl(aclA);
  applyAcl(aclB);

  const merged: IssueAclMap = {};
  identityAccessMap.forEach((access, identity) => {
    if (!merged[access]) {
      merged[access] = [];
    }
    merged[access]!.push(identity);
  });
  return normalizeIssueAcl(merged);
};

export const getAvatarInitials = (displayName: string): string => {
  const parts = displayName.trim().split(/\s+/);
  if (parts.length >= AVATAR_INITIALS_MIN_NAME_PARTS) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return displayName
    .substring(0, AVATAR_INITIALS_FALLBACK_LENGTH)
    .toUpperCase();
};

interface UserLookupMaps {
  readonly byTiduuid: Map<string, IProjectUser>;
  readonly byId: Map<string, IProjectUser>;
}

const buildUserLookupMaps = (
  projectUsers: readonly IProjectUser[]
): UserLookupMaps => {
  const byTiduuid = new Map<string, IProjectUser>();
  const byId = new Map<string, IProjectUser>();
  for (const user of projectUsers) {
    if (user.tiduuid) byTiduuid.set(user.tiduuid, user);
    if (user.id) byId.set(user.id, user);
  }
  return { byTiduuid, byId };
};

const resolveIdentityName = (
  identity: string,
  lookupMaps: UserLookupMaps
): { displayName: string; isGroup: boolean } => {
  if (identity === PERMISSION_CONSTANTS.ALL_PROJECT_MEMBERS_ID) {
    return { displayName: 'All project members', isGroup: true };
  }

  if (identity.startsWith(PERMISSION_CONSTANTS.USER_PREFIX)) {
    const tiduuid = identity.replace(PERMISSION_CONSTANTS.USER_PREFIX, '');
    const user =
      lookupMaps.byTiduuid.get(tiduuid) ?? lookupMaps.byId.get(tiduuid);
    if (user?.firstName && user?.lastName) {
      return {
        displayName: `${user.firstName} ${user.lastName}`,
        isGroup: false,
      };
    }
    return { displayName: tiduuid, isGroup: false };
  }

  if (identity.startsWith(PERMISSION_CONSTANTS.GROUP_PREFIX)) {
    const groupId = identity.replace(PERMISSION_CONSTANTS.GROUP_PREFIX, '');
    const group = lookupMaps.byId.get(groupId);
    if (group?.firstName) {
      return { displayName: group.firstName, isGroup: true };
    }
    return { displayName: groupId, isGroup: true };
  }

  return {
    displayName: translate('TC.Common.Unknown') || 'Unknown user',
    isGroup: false,
  };
};

export const resolvePermissionsToEntries = (
  acl: IssueAclMap,
  inheritedAcl: IssueAclMap | undefined,
  projectUsers: readonly IProjectUser[]
): { direct: IPermissionEntry[]; inherited: IPermissionEntry[] } => {
  const lookupMaps = buildUserLookupMaps(projectUsers);
  const inheritedIdentities = new Set<string>();
  const inherited: IPermissionEntry[] = [];

  if (inheritedAcl) {
    ISSUE_ACCESS_BUCKETS.forEach((bucket) => {
      inheritedAcl[bucket]?.forEach((identity) => {
        if (inheritedIdentities.has(identity)) return;
        inheritedIdentities.add(identity);
        const { displayName, isGroup } = resolveIdentityName(
          identity,
          lookupMaps
        );
        inherited.push({
          identity,
          displayName,
          accessLevel: bucket,
          isInherited: true,
          avatarInitials: getAvatarInitials(displayName),
          isGroup,
        });
      });
    });
  }

  const direct: IPermissionEntry[] = [];
  const seenDirect = new Set<string>();

  ISSUE_ACCESS_BUCKETS.forEach((bucket) => {
    acl[bucket]?.forEach((identity) => {
      if (seenDirect.has(identity) || inheritedIdentities.has(identity)) return;
      seenDirect.add(identity);

      const { displayName, isGroup } = resolveIdentityName(
        identity,
        lookupMaps
      );
      direct.push({
        identity,
        displayName,
        accessLevel: bucket,
        isInherited: false,
        avatarInitials: getAvatarInitials(displayName),
        isGroup,
      });
    });
  });

  return { direct, inherited };
};

const entryToIdentity = (entry: IPermissionEntry): string => entry.identity;

const VALID_IDENTITY_REGEX = /^(users:|tc-groups:).+$/;

const isValidIdentity = (identity: string): boolean =>
  VALID_IDENTITY_REGEX.test(identity);

export const buildIssueAclPayload = (
  permissions: readonly IPermissionEntry[]
): { acl: IssueAclMap } => {
  const acl: IssueAclMap = {};

  permissions.forEach((permission) => {
    if (!isValidIdentity(permission.identity)) return;
    const bucket = permission.accessLevel;
    if (!acl[bucket]) {
      acl[bucket] = [];
    }
    acl[bucket]!.push(permission.identity);
  });

  return { acl: normalizeIssueAcl(acl) };
};

export const prepareMultiIssuePermissionDetails = (
  perIssuePermissions: ReadonlyArray<{
    readonly direct: readonly IPermissionEntry[];
    readonly inherited: readonly IPermissionEntry[];
  }>
): { consolidated: IPermissionEntry[]; inheritedUnion: IPermissionEntry[] } => {
  const totalIssues = perIssuePermissions.length;
  const accessByIdentity = new Map<string, Set<IssueAccessBucket>>();
  const appearanceByIdentity = new Map<string, number>();

  perIssuePermissions.forEach(({ direct }) => {
    direct.forEach((entry) => {
      const identity = entryToIdentity(entry);
      if (!accessByIdentity.has(identity)) {
        accessByIdentity.set(identity, new Set<IssueAccessBucket>());
        appearanceByIdentity.set(identity, 0);
      }
      accessByIdentity.get(identity)!.add(entry.accessLevel);
      appearanceByIdentity.set(
        identity,
        (appearanceByIdentity.get(identity) ?? 0) + 1
      );
    });
  });

  const consolidated: IPermissionEntry[] = [];
  const seenIdentities = new Set<string>();

  perIssuePermissions.forEach(({ direct }) => {
    direct.forEach((entry) => {
      const identity = entryToIdentity(entry);
      if (seenIdentities.has(identity)) return;
      seenIdentities.add(identity);

      const accessSet =
        accessByIdentity.get(identity) ?? new Set<IssueAccessBucket>();
      const appearances = appearanceByIdentity.get(identity) ?? 0;
      const isMixed = appearances < totalIssues || accessSet.size > 1;

      const [resolvedAccess] = accessSet;
      consolidated.push({
        ...entry,
        accessLevel:
          isMixed || !resolvedAccess ? entry.accessLevel : resolvedAccess,
        isMixed,
      });
    });
  });

  const inheritedUnion: IPermissionEntry[] = [];
  const seenInherited = new Set<string>();
  perIssuePermissions.forEach(({ inherited }) => {
    inherited.forEach((entry) => {
      const identity = entryToIdentity(entry);
      if (seenInherited.has(identity)) return;
      seenInherited.add(identity);
      inheritedUnion.push(entry);
    });
  });

  return { consolidated, inheritedUnion };
};

export const buildBatchIssueAclForIssue = (
  displayedPermissions: readonly IPermissionEntry[],
  originalIssuePermissions: readonly IPermissionEntry[]
): { acl: IssueAclMap } => {
  const originalByIdentity = new Map(
    originalIssuePermissions.map((item) => [entryToIdentity(item), item])
  );
  const finalList: IPermissionEntry[] = [];

  displayedPermissions.forEach((permission) => {
    if (!permission.isMixed) {
      finalList.push(permission);
      return;
    }
    const original = originalByIdentity.get(entryToIdentity(permission));
    if (original) {
      finalList.push(original);
    }
  });

  return buildIssueAclPayload(finalList);
};

export const countIssueAclIdentities = (acl: IssueAclMap): number => {
  const identitySet = new Set<string>();
  ISSUE_ACCESS_BUCKETS.forEach((bucket) => {
    acl[bucket]?.forEach((identity) => identitySet.add(identity));
  });
  return identitySet.size;
};

const getAccessLabel = (access: IssueAccessBucket): string =>
  access === 'edit'
    ? translate('TC.Common.EditAccess') || 'Edit access'
    : translate('TC.Common.ReadOnly') || 'Read only';

export const normalizeToFullIdentity = (
  raw: string | undefined
): string | undefined => {
  if (!raw) return undefined;
  return raw.startsWith(PERMISSION_CONSTANTS.USER_PREFIX)
    ? raw
    : `${PERMISSION_CONSTANTS.USER_PREFIX}${raw}`;
};

export const buildSharedWithChips = (
  acl: IssueAclMap,
  inheritedAcl: IssueAclMap | undefined,
  projectUsers: readonly IProjectUser[],
  creatorIdentity?: string
): string[] => {
  const creatorFullIdentity = normalizeToFullIdentity(creatorIdentity);
  const lookupMaps = buildUserLookupMaps(projectUsers);
  const inheritedIdentities = new Set<string>();
  if (inheritedAcl) {
    ISSUE_ACCESS_BUCKETS.forEach((bucket) => {
      inheritedAcl[bucket]?.forEach((identity) =>
        inheritedIdentities.add(identity)
      );
    });
  }

  const effectiveAccess = new Map<string, IssueAccessBucket>();

  const applyAcl = (sourceAcl: IssueAclMap | undefined): void => {
    if (!sourceAcl) return;
    ISSUE_ACCESS_BUCKETS.forEach((bucket) => {
      sourceAcl[bucket]?.forEach((identity) => {
        const current = effectiveAccess.get(identity);
        if (!current || getAccessWeight(bucket) > getAccessWeight(current)) {
          effectiveAccess.set(identity, bucket);
        }
      });
    });
  };

  applyAcl(inheritedAcl);
  applyAcl(acl);

  const chips: string[] = [];
  effectiveAccess.forEach((access, identity) => {
    const { displayName } = resolveIdentityName(identity, lookupMaps);
    let label: string;
    if (inheritedIdentities.has(identity)) {
      label =
        identity === creatorFullIdentity
          ? translate('TC.Common.FullAccess') || 'Full access'
          : translate('TC.Common.EditAccess') || 'Edit access';
    } else {
      label = getAccessLabel(access);
    }
    chips.push(`${displayName} (${label})`);
  });

  return chips.sort((a, b) => a.localeCompare(b));
};
